import type { PlayerDto } from './player.dto.js';

export enum OfferEventType
{
  PlacementImpression = 'placement_impression',
  Shown            = 'shown',
  Clicked          = 'clicked',
  Close            = 'close',
  PriceLoaded      = 'price_loaded',
  PriceUnavailable = 'price_unavailable',
  PurchaseStarted  = 'purchase_started',
  PurchaseSuccess  = 'purchase_success',
  PurchaseFailed   = 'purchase_failed',
  FreeClaim        = 'free_claim',
}

export enum OfferStore
{
  AppStore   = 'app_store',
  GooglePlay = 'google_play',
  Sandbox    = 'sandbox',
  /** Paid inside the Telegram Mini App; the bot webhook, not the client, confirms the payment. */
  TelegramStars = 'telegram_stars',
  /** Paid with TON or USDT through TON Connect on the web build; the chain watcher, not the client, confirms the payment. */
  Ton = 'ton',
}

export enum OfferPlacement
{
  Shop  = 'shop',
  Offer = 'offer',
}

/** trigger_dialog is gone: an event-driven dialog is a placement_dialog with entry point auto_dialog and a trigger. */
export type OfferKind = 'static_shop' | 'placement_dialog' | 'chained';
export type OfferShopSection = 'bundles' | 'gold' | 'deals';

/** Server-validated rendering preset; see offer_visual_registry for what each one draws. */
export type OfferVisual = string;
export type OfferBadge = 'best';

/** Localization keys overriding the visual's default copy. */
export interface OfferTextsDto
{
  title_key?: string;
  description_key?: string;
  button_key?: string;
}

export type OfferPurchaseLimitResetDto =
  | { type: 'none' }
  | { type: 'interval'; seconds: number }
  | { type: 'calendar'; period: 'daily' | 'weekly' | 'monthly'; hour_utc: number };

/** Purchases allowed per cycle and when the cycle resets. */
export interface OfferPurchaseLimitDto
{
  max: number;
  reset: OfferPurchaseLimitResetDto;
}

export type OfferRewardType =
  | 'coins'
  | 'hearts'
  | 'big_balls'
  | 'infinite_balls'
  | 'multi_balls'
  | 'fireworks'
  | 'infinite_hearts_minutes'
  // | 'no_ads_minutes'
  /** Extra shots for the level being played; only under the continue_dialog placement. */
  | 'level_shots'
  // | 'no_ads';

export interface OfferRewardDto
{
  type: OfferRewardType;
  amount: number;
}

export type OfferRewardsDto = Partial<Record<Exclude<OfferRewardType, 'no_ads'>, number>> & { no_ads?: true };

export type OfferTriggerEvent =
  | 'session_started'
  | 'level_started'
  | 'level_won'
  | 'level_failed'
  | 'hearts_depleted'
  | 'rewarded_ad_completed'
  | 'interstitial_shown'
  | 'booster_used'
  | 'purchase_completed'
  | 'shop_opened'
  | 'shop_closed'
  | 'continue_dialog_opened';

/** `rewarded`: claimed like a free offer once the ad SDK confirms a completed rewarded video. */
export type OfferPurchaseType = 'consumable' | 'non_consumable' | 'free' | 'rewarded';

export interface OfferProductDto
{
  productId: string;
  packageName?: string;
  bundleId?: string;
  environment?: 'production' | 'sandbox';
  autoAcknowledge?: boolean;
  /** Base USD price of the SKU synced by the server from Google Play; reported to analytics as `originalusd`. */
  priceUsd?: number;
  /** Telegram product only: the price in Stars the invoice will charge. */
  priceStars?: number;
  /** TON product only: fixed catalog prices; either may be absent. */
  priceUsdt?: number;
  priceTon?: number;
}

export interface OfferProductsDto
{
  ios?: OfferProductDto;
  android?: OfferProductDto;
  telegram?: OfferProductDto;
  ton?: OfferProductDto;
}

/** A paid chain button and the complete reward package it grants. */
export interface ChainOfferPurchaseDto
{
  stepIndex: number;
  stepId: string;
  rewards: OfferRewardsDto;
}

export interface ChainOfferStepDto
{
  id: string;
  free: boolean;
  /** A non-store step that must be unlocked by a completed rewarded video. */
  rewarded?: boolean;
  rewards: OfferRewardsDto;
  /** Present only for a store-purchase step. */
  products?: OfferProductsDto;
  /** Catalog product the step sells and its USD price (analytics `priceusd` / `originalusd`). */
  product?: string;
  price_usd?: number;
}

export interface OfferChainDto
{
  /** After the final paid package, the server starts this chain from the first package again. */
  repeat?: boolean;
  steps: ChainOfferStepDto[];
  purchases: ChainOfferPurchaseDto[];
  /** Current card to interact with; it may be a paid or a free step. */
  next_step_id?: string | null;
  /** @deprecated: use next_step_id. Kept during the server/client rollout. */
  next_paid_step_id?: string | null;
}

export interface OfferDto
{
  id: string;
  entitlement_key: string;
  enabled: boolean;
  priority: number;
  kind?: OfferKind;
  rewards?: OfferRewardsDto;
  presentation?: OfferKind;
  shop_section?: OfferShopSection;
  visual?: OfferVisual;
  badge?: OfferBadge;
  texts?: OfferTextsDto;
  /** Catalog product this offer sells and its USD price (analytics `priceusd` / `originalusd`). */
  product?: string;
  price_usd?: number;
  starts_at?: string;
  ends_at?: string;
  /** Server deadline for a window that starts when this offer is first shown in the current session. */
  session_ends_at?: string;
  /** Server deadline for a window that starts once when this offer is first shown by this player. */
  first_shown_ends_at?: string;
  shown_duration_seconds?: number;
  first_shown_duration_seconds?: number;
  /** Display-only saving percentage; the store SKU remains the charged price. */
  discount_percent?: number;
  products: OfferProductsDto;
  purchase?: { type: OfferPurchaseType };
  /** Server-enforced number of successful purchases/claims allowed per player. */
  max_claims?: number;
  purchase_limit?: OfferPurchaseLimitDto;
  /** Purchases still allowed in the current cycle; absent when the offer is unlimited. */
  purchases_left?: number;
  /** Server deadline after which the current cycle's count drops back to zero. */
  limit_resets_at?: string;
  chain?: OfferChainDto;
  placement?: OfferPlacementConfigDto;
  /** Present on placements served through a trigger decision. */
  trigger?: {
    events: OfferTriggerEvent[];
    threshold: number;
    cooldown_seconds: number;
    max_shows?: number;
    max_purchases?: number;
    once_per_level?: boolean;
    once_per_session?: boolean;
    booster_types?: string[];
  };
  owned?: boolean;
  data?: Record<string, unknown>;
}

export interface OfferPlacementConfigDto
{
  id: string;
  template: string;
  min_level: number;
}

/** A server-selected offer for a concrete client entry point. */
export interface ResolvedOfferPlacementDto
{
  offer_id: string;
  template: string;
}

export interface OffersResponse
{
  offers: OfferDto[];
  placements?: Record<string, ResolvedOfferPlacementDto>;
  entitlements: Record<string, boolean | EntitlementDto>;
  /** UTC clock sampled by the offers endpoint for timed-offer presentation. */
  server_time?: string;
  /** Open and just-finished TON orders of this player, for resuming without local storage. */
  ton_orders?: TonOrderSummaryDto[];
}

export interface EntitlementDto
{
  active: boolean;
  expires_at?: string | null;
  source_offer_id?: string | null;
}

export interface ValidatePurchaseRequest
{
  store: OfferStore;
  product_id: string;
  /** Identifies a paid chain step when multiple steps use the same SKU. */
  chain_step_id?: string;
  /** Server-issued trigger decision that authorized a trigger-dialog purchase. */
  trigger_event_id?: string;
  /** Server-resolved manual entry point that authorized a placement offer purchase. */
  placement_id?: string;
  transaction_id?: string;
  original_transaction_id?: string;
  purchase_token?: string;
  package_name?: string;
  bundle_id?: string;
  currency?: string;
  price_micros?: number | string;
  environment?: 'production' | 'sandbox';
  /** Test-only platform selector for a sandbox purchase with distinct iOS/Android SKUs. */
  sandbox_platform?: 'ios' | 'android';
  /** Ephemeral client session used for server-authoritative relative offer windows. */
  session_id?: string;
}

export interface ValidatePurchaseResponse
{
  ok: boolean;
  player?: PlayerDto;
  entitlement: {
    key: string;
    active: boolean;
    expires_at?: string | null;
  } | null;
  purchase: {
    id: number;
    offer_id: string;
    product_id: string;
    store: OfferStore;
    status: string;
    transaction_id?: string | null;
    purchase_type?: string | null;
    is_test?: boolean;
    environment?: 'production' | 'sandbox';
    /** Store currency of the charged price; for App Store it comes from the signed transaction. */
    currency?: string | null;
    price_micros?: number | string | null;
    /** USD price the server reports for analytics (`priceusd`): the catalog product price. */
    price_usd?: number | null;
    /** Configured USD price of the SKU/step (`originalusd`). */
    original_usd?: number | null;
    /** Upstream alias of original_usd (base Google Play USD price at purchase time). */
    original_price_usd?: number | null;
    /** Currently equal to price_usd; net-of-fee if analytics ever asks for it. */
    revenue_usd?: number | null;
    /** 1-based chain step position. */
    offer_step?: number;
    price_source?: 'store' | 'client' | 'catalog' | null;
  };
  granted?: OfferRewardsDto & { infinite_hearts_until?: string; no_ads_until?: string };
  grant_applied?: boolean;
  next_step_id?: string | null;
  next_paid_step_id?: string | null;
  purchases_left?: number;
  limit_resets_at?: string;
}

export interface ClaimFreeOfferResponse
{
  ok: boolean;
  duplicate?: boolean;
  player?: PlayerDto;
  granted?: OfferRewardsDto & { infinite_hearts_until?: string; no_ads_until?: string };
  grant_applied?: boolean;
  claim_count?: number;
  purchases_left?: number;
  limit_resets_at?: string;
}

export interface OfferTriggerDecision
{
  action: 'show_offer' | 'none';
  reason?: string;
  offer?: OfferDto;
}

export interface OfferTriggerEventRequest
{
  event_id: string;
  event: OfferTriggerEvent;
  session_id?: string;
  level?: number;
  value?: number;
  occurred_at?: string;
  context?: Record<string, unknown>;
}

export interface OfferTriggerEventResponse
{
  ok: boolean;
  event_id: string;
  duplicate: boolean;
  decision: OfferTriggerDecision;
}

export type TelegramStarsOrderStatus = 'pending' | 'paid' | 'fulfilled' | 'expired' | 'failed' | 'refunding' | 'refunded';

export interface TelegramStarsInvoiceRequest
{
  product_id?: string;
  chain_step_id?: string;
  placement_id?: string;
  trigger_event_id?: string;
  session_id?: string;
  /** Shown in Telegram's payment sheet; the server bounds and cleans both. */
  title?: string;
  description?: string;
}

export interface TelegramStarsOrderDto
{
  ok: boolean;
  order_id: string;
  status: TelegramStarsOrderStatus;
  offer_id: string;
  product_id: string;
  chain_step_id?: string | null;
  amount_stars: number;
  currency: 'XTR';
  invoice_link?: string | null;
  expires_at?: string;
}

export interface TelegramStarsInvoiceResponse extends TelegramStarsOrderDto
{
  reused: boolean;
}

export interface TelegramStarsOrderResponse extends TelegramStarsOrderDto
{
  /** Present once the order is fulfilled. */
  purchase?: ValidatePurchaseResponse['purchase'] | null;
  granted?: ValidatePurchaseResponse['granted'] | null;
  player?: PlayerDto | null;
}

// ---------------------------------------------------------------------------
// TON / USDT (TON Connect)
// ---------------------------------------------------------------------------

export type TonAsset = 'TON' | 'USDT';
export type TonOrderStatus = 'pending' | 'underpaid' | 'paid' | 'fulfilled' | 'expired' | 'failed' | 'refunding' | 'refunded';
/** What the player is waiting for, derived by the server from the ledger. */
export type TonOrderStage = 'awaiting_payment' | 'payment_seen' | 'granting' | 'done' | 'closed';

export interface TonInvoiceRequest
{
  asset: TonAsset;
  product_id?: string;
  chain_step_id?: string;
  placement_id?: string;
  trigger_event_id?: string;
  session_id?: string;
  /** Connected wallet address; required for USDT (the transfer is built for its jetton wallet). */
  sender_address?: string;
  /** The player accepted immediate delivery and waived the withdrawal right. */
  withdrawal_waiver: true;
  withdrawal_waiver_version?: string;
}

/** TON Connect SendTransactionRequest built by the server. */
export interface TonSendTransactionRequest
{
  validUntil: number;
  network: '-239' | '-3';
  from?: string;
  messages: { address: string; amount: string; payload: string }[];
}

export interface TonOrderDto
{
  ok: boolean;
  order_id: string;
  status: TonOrderStatus;
  stage: TonOrderStage;
  offer_id: string;
  product_id: string;
  chain_step_id?: string | null;
  asset: TonAsset;
  /** Smallest units (nanoton / micro-USDT) as a decimal string. */
  amount: string;
  amount_display: string;
  amount_usd: number;
  paid_amount: string;
  expires_at: string;
  grace_until: string;
  valid_until?: string;
  sent_at?: string;
  late_possible: boolean;
  seen_tx_hash?: string;
  explorer_url?: string;
  extra_payments?: { tx_hash: string; kind: string; asset: TonAsset; amount: string; explorer_url: string }[];
  paid_at?: string;
  fulfilled_at?: string;
  acknowledged: boolean;
  poll_after_ms: number;
}

export interface TonInvoiceResponse extends TonOrderDto
{
  reused: boolean;
  transaction: TonSendTransactionRequest;
}

export interface TonTransactionResponse extends TonOrderDto
{
  transaction: TonSendTransactionRequest;
}

export interface TonOrderResponse extends TonOrderDto
{
  purchase?: ValidatePurchaseResponse['purchase'] | null;
  granted?: ValidatePurchaseResponse['granted'] | null;
  player?: PlayerDto | null;
}

export interface TonOrderSummaryDto
{
  order_id: string;
  offer_id: string;
  chain_step_id?: string;
  asset: TonAsset;
  status: TonOrderStatus;
  stage: TonOrderStage;
  expires_at: string;
  grace_until: string;
  acknowledged: boolean;
}

/** Error body of a 409 from the invoice endpoint; amounts are display strings in the asset's units. */
export interface TonInvoiceErrorBody
{
  error: string;
  message?: string;
  asset?: TonAsset;
  required?: string;
  available?: string;
}
